/**
 * `LivelloBadge` — rendering in **sola lettura** di un Livello (`e-1..e-5`) con
 * la relativa etichetta.
 *
 * Design ref: "Componenti chiave" → un piccolo componente `LivelloBadge`
 * riutilizzabile centralizza rendering ed etichette; il pannello valutatore
 * (`ReviewPanel.tsx`, task 9.4) lo usa per mostrare il `livelloSelf` dichiarato
 * dallo User accanto a ciascuna Skill (Req. 6.2). *Validates: Requirements 1.2, 1.3.*
 *
 * La scala Livello (`LIVELLO_LABELS`, tipo `Livello`) è importata da
 * `shared/dto`: il componente NON ridefinisce la scala né duplica le etichette.
 *
 * Gestisce esplicitamente il caso `livello === null` (nessun Livello
 * dichiarato), mostrando un indicatore di assenza neutro anziché una badge
 * vuota, coerente con la presentazione dei valori mancanti nell'app.
 */
import { LIVELLO_LABELS, type Livello } from '../shared/dto';
/** Indicatore mostrato quando nessun Livello è dichiarato (`null`). */
export const LIVELLO_ABSENT_INDICATOR = '—';

export interface LivelloBadgeProps {
  /** Livello da mostrare, oppure `null` se non dichiarato. */
  livello: Livello | null;
  /** Prefisso accessibile (es. "Livello"), per la label dello screen reader. */
  label?: string;
}

/**
 * Mostra il Livello come **chip**: un piccolo quadratino con il codice
 * (`e-1..e-5`) a sinistra e l'etichetta descrittiva a destra, dentro una pill
 * neutra. Con `livello === null` mostra l'indicatore di assenza con un'etichetta
 * accessibile dedicata.
 */
export function LivelloBadge({ livello, label = 'Livello' }: LivelloBadgeProps) {
  if (livello === null) {
    return (
      <span
        className="livello-badge livello-badge--absent"
        aria-label={`${label}: non dichiarato`}
      >
        {LIVELLO_ABSENT_INDICATOR}
      </span>
    );
  }

  const text = LIVELLO_LABELS[livello];
  return (
    <span className="level-chip" aria-label={`${label}: ${text}`}>
      <span className="level-chip__code" aria-hidden="true">
        {livello}
      </span>
      <span className="level-chip__label">{text}</span>
    </span>
  );
}

export default LivelloBadge;
